import { useState } from 'react';
import { Alert, App, Button, Input, Modal } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ArchiveTier, ArrangementDto, RevertArrangementDto, RevertCountsDto, RevertImpactDto } from '@pams/shared';
import { useAuth } from '@/hooks/useAuth';
import { mayRevertRunning } from '@/lib/accessRole';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { REVERT_COPY } from './copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P7B §6b/§6c] TAKING A RUNNING ORDER BACK TO DRAFT — `UnpublishAction`'s SHAPE, FOR THE ONE CASE
 *  THAT CONTROL REFUSES.
 *
 *  Offered only to a `master_superadmin` holder (`mayRevertRunning`, the route's own requirement), on
 *  an order that is issued, not archived, and whose archive tier is `BLOCKED` — a duty has started and
 *  one is unfinished. That is exactly where the ordinary Take back to draft says "this cannot be taken
 *  back", and nowhere else: before a duty starts the ordinary act applies, and a finished order is a
 *  record that stays. The tier is the page's own `delete-impact` read, so this costs no request to
 *  decide whether to render.
 *
 *  ⚠️ A CONTROLLED `<Modal>`, NEVER `modal.confirm` — the reason is a REQUIRED, validated input, and
 *  `modal.confirm`'s `onOk` cannot hold the dialog open on an empty field (§8). It is therefore an
 *  overlay with its own `census.spec.ts` exemption, `UnpublishAction`'s reasoning verbatim.
 *
 *  ⚠️ THE IMPACT IS READ ONLY WHEN THE DIALOG OPENS — zero requests for a non-holder, none for a holder
 *  who never opens it — and EVERY COUNT IT STATES IS ECHOED BACK as `expected`. The server counts again
 *  inside its transaction and answers 409 if anything moved; this then re-reads the counts and says so,
 *  rather than taking back more than the person confirmed.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
const RUNNING: readonly ArrangementDto['status'][] = ['PUBLISHED', 'ALLOCATION_IN_PROGRESS', 'ACTIVE'];

const countsOf = (impact: RevertImpactDto): RevertCountsDto => ({
  deploymentCount: impact.deploymentCount,
  emptySlotCount: impact.emptySlotCount,
  namedSlotCount: impact.namedSlotCount,
  namedOfficerCount: impact.namedOfficerCount,
  dutyEventCount: impact.dutyEventCount,
  dutyNotificationCount: impact.dutyNotificationCount,
});

export const RevertRunningAction = ({
  arrangementId,
  status,
  archived,
  tier,
  onDone,
}: {
  arrangementId: string;
  status: ArrangementDto['status'] | undefined;
  archived: boolean;
  /** The page's own `delete-impact` tier — `undefined` while it loads, which renders nothing. */
  tier: ArchiveTier | undefined;
  onDone: () => void;
}): React.ReactElement | null => {
  const { officer } = useAuth();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [changed, setChanged] = useState(false);

  const offered =
    mayRevertRunning(officer) && !archived && status !== undefined && RUNNING.includes(status) && tier === 'BLOCKED';

  const impact = useQuery({
    queryKey: queryKeys.arrangements.revertImpact(arrangementId),
    queryFn: ({ signal }) => api.get<RevertImpactDto>(`/arrangements/${arrangementId}/revert-impact`, { signal }),
    enabled: open && offered,
    staleTime: 0,
  });

  /* ⚠️ [LIFECYCLE]'s rule: the reset belongs to the commit handler AND the dismissal, in one function. */
  const close = (): void => {
    setOpen(false);
    setReason('');
    setChanged(false);
  };

  const revert = useMutation({
    mutationFn: (body: RevertArrangementDto) => api.post<ArrangementDto>(`/arrangements/${arrangementId}/revert`, body),
    onSuccess: () => {
      void message.success(REVERT_COPY.done);
      close();
      onDone();
      void queryClient.invalidateQueries({ queryKey: queryKeys.arrangements.all });
    },
    onError: (error: ApiError) => {
      if (error instanceof ApiError && error.status === 409) {
        setChanged(true);
        void impact.refetch();
        return;
      }
      void message.error(error.message);
    },
  });

  if (!offered) return null;

  const data = impact.data;
  const refused = data !== undefined && !data.eligible;

  return (
    <>
      <Button
        danger
        data-testid="revert-running"
        onClick={() => {
          setOpen(true);
        }}
      >
        {REVERT_COPY.action}
      </Button>

      <Modal
        open={open}
        title={REVERT_COPY.title}
        okText={REVERT_COPY.ok}
        okButtonProps={{
          danger: true,
          disabled: data === undefined || refused || impact.isFetching || reason.trim() === '',
          'data-testid': 'revert-confirm',
        }}
        confirmLoading={revert.isPending}
        onCancel={close}
        onOk={() => {
          if (data === undefined) return;
          setChanged(false);
          revert.mutate({ reason: reason.trim(), expected: countsOf(data) });
        }}
        data-testid="revert-modal"
      >
        <div className="flex flex-col gap-2 text-sm">
          {changed ? (
            <Alert type="warning" showIcon data-testid="revert-changed" message={REVERT_COPY.changed} />
          ) : null}
          {impact.isFetching && data === undefined ? (
            <p className="text-muted-foreground">{REVERT_COPY.checking}</p>
          ) : data === undefined ? null : refused ? (
            <Alert type="error" showIcon data-testid="revert-refused" message={data.refusal ?? REVERT_COPY.title} />
          ) : (
            <ul className="flex flex-col gap-1" data-testid="revert-counts">
              <li className="text-muted-foreground">{REVERT_COPY.intro}</li>
              <li data-testid="revert-duties">{REVERT_COPY.duties(data.deploymentCount)}</li>
              <li data-testid="revert-empty">{REVERT_COPY.emptySlots(data.emptySlotCount)}</li>
              <li data-testid="revert-names">{REVERT_COPY.names(data.namedSlotCount, data.namedOfficerCount)}</li>
              <li data-testid="revert-markings">{REVERT_COPY.markings(data.dutyEventCount)}</li>
              <li data-testid="revert-notifications">{REVERT_COPY.notifications(data.dutyNotificationCount)}</li>
              <li data-testid="revert-incharge">
                {data.inChargeNamed ? REVERT_COPY.inChargeNamed : REVERT_COPY.inChargeNotNamed}
              </li>
              <li className="text-muted-foreground">{REVERT_COPY.told}</li>
            </ul>
          )}
          {refused ? null : (
            <label className="flex flex-col gap-1">
              <span className="font-medium">{REVERT_COPY.reasonLabel}</span>
              <Input
                value={reason}
                maxLength={500}
                onChange={(event) => {
                  setReason(event.target.value);
                }}
                data-testid="revert-reason"
                aria-label={REVERT_COPY.reasonLabel}
              />
              <span className="text-xs text-muted-foreground">{REVERT_COPY.reasonHint}</span>
            </label>
          )}
        </div>
      </Modal>
    </>
  );
};
